/* eslint-disable vue/no-unused-components */
<template>
  <div class="mt-3 container project-list-page">
    <div class="mb-3 d-flex align-items-center">
      <h1 class="m-0">Projects</h1>
      <div class="ms-auto">
        <div class="search-project-input-wrapper">
          <input
            type="text"
            v-model="searchProject"
            class="form-control"
            placeholder="Search"
          />
          <Button
            v-if="searchProject !== ''"
            :dynamicAttrs="{class: 'blank-button btn-clear-search'}"
            @click="clearSearchInput"
          >
            <CloseCircleFilled />
          </Button>
          <SearchOutlined />
        </div>
        <create-project-button :disabled="loading" />
      </div>
    </div>

    <div class="text-center" v-if="!initialized || loading">
      <Loading content="Loading" />
    </div>

    <div v-else class="row">
      <a-config-provider>
        <template #renderEmpty>
          <div style="text-align: center">
            <template v-if="searchProject === ''">
              <rocket-outlined style="font-size: 100px; margin-top: 90px; margin-bottom: 90px;" />
              <p class="description-no-data">There is nothing here. Create your first project to  get started.</p>
            </template>
            <template v-if="searchProject !== ''">
              <p class="description-no-data" style="height: 200px; line-height: 200px">No matching projects</p>
            </template>
          </div>
        </template>
        <div class="config-provider">
          <a-table
            :columns="columns"
            :data-source="filteredProject"
            :pagination="false"
            size="middle"
            class="tbl-header-white"
            rowKey="id"
          >
            <template #id="{ record }">
              <router-link class="nav-link" :to="'/projects/' + record.slug">{{record.id}}</router-link>
            </template>
            <template #description="{ record }">
              <router-link class="nav-link" :to="'/projects/' + record.slug">{{record.description || '&nbsp'}}</router-link>
            </template>
            <template #name="{ record }">
              <router-link class="nav-link" :to="'/projects/' + record.slug">{{record.name}}</router-link>
            </template>
            <template #updatedAt="{ record }">
              <router-link class="nav-link" :to="'/projects/' + record.slug">{{ fromNow(record.updatedAt) }}</router-link>
            </template>
            <template #action="{ record }">
              <a-dropdown :trigger="['click']" overlayClassName="project-list-action">
                <span class="btn-action">
                  <EllipsisOutlined class="dot-action" />
                </span>
                <template #overlay>
                <div class="list-action">
                  <ModalConfirm
                    :title="'Are you sure want to delete project ' + record.name + ' ?'"
                    content="This action is permanent! It cannot be undone"
                    :ok="deleteProject(record.slug)"
                    okText="Delete"
                    cancelText="Cancel"
                  >
                      <div class="action-item txt-red">
                        Delete
                      </div>
                  </ModalConfirm>
                </div>
              </template>
              </a-dropdown>
            </template>
          </a-table>
        </div>
      </a-config-provider>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import moment from 'moment';
import filter from 'lodash/fp/filter';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Button from '@/components/Button/template.vue';
import Table from '@/components/Table/template.vue';
import Dropdown from '@/components/Dropdown/template.vue';
import Empty from '@/components/Empty/template.vue';
import Loading from '@/components/Loading/template.vue';
import ModalConfirm from '@/components/ModalConfirm/template.vue';
import ConfigProvider from '@/components/ConfigProvider/template.vue';
import SearchOutlined from '@ant-design/icons-vue/SearchOutlined';
import CloseCircleFilled from '@ant-design/icons-vue/CloseCircleFilled';
import RocketOutlined from '@ant-design/icons-vue/RocketOutlined';
import EllipsisOutlined from '@ant-design/icons-vue/EllipsisOutlined';
import Project from '../../models/Project';
import { projectsMapper } from '../../store';
import CreateProjectButton from '../../components/CreateProjectButton.vue';
import './styles.scss';

export default defineComponent({
  name: 'ProjectsList',
  components: {
    CreateProjectButton,
    Button,
    SearchOutlined,
    CloseCircleFilled,
    EllipsisOutlined,
    Table,
    Dropdown,
    Empty,
    ConfigProvider,
    RocketOutlined,
    ModalConfirm,
    Loading,
  },
  data() {
    return {
      searchProject: '',
      loading: true,
      isShowConfirmDelete: false,
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: '20%',
          ellipsis: true,
          slots: { customRender: 'id' },
          sorter: (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name),
        },
        {
          title: 'NAME',
          dataIndex: 'name',
          key: 'name',
          ellipsis: true,
          slots: { customRender: 'name' },
          sorter: (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name),
        },
        {
          title: 'DESCRIPTION',
          dataIndex: 'description',
          key: 'description',
          ellipsis: true,
          slots: { customRender: 'description' },
          sorter: (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name),
        },
        {
          title: 'UPDATED AT',
          dataIndex: 'updatedAt',
          key: 'updatedAt',
          width: 150,
          slots: { customRender: 'updatedAt' },
          sorter: (a: { updatedAt: any }, b: { updatedAt: any }) => moment(b.updatedAt).valueOf() - moment(a.updatedAt).valueOf(),
        },
        {
          key: 'action',
          slots: { customRender: 'action' },
          width: '10%',
        },
      ],
    };
  },
  computed: {
    ...projectsMapper.mapState(['projects', 'initialized']),
    filteredProject(): Array<Project> {
      return filter((project) => project.name.search(this.searchProject) !== -1, this.projects);
    },
  },
  methods: {
    ...projectsMapper.mapActions(['index', 'delete']),
    async fetchProjects(force = false) {
      this.loading = true;
      this.index(force);
      this.loading = false;
    },
    fromNow(time: string) {
      return moment(time).fromNow();
    },
    clearSearchInput() {
      this.searchProject = '';
    },
    deleteProject(projectSlug: string) {
      return () => {
        this.delete(projectSlug);
      };
    },
    toggleConfirmDelete() {
      this.isShowConfirmDelete = !this.isShowConfirmDelete;
    },
  },
  mounted() {
    this.fetchProjects();
  },
});
</script>
